// src/components/AIAssistant.js
// 浮動 AI 助手聊天介面
import { useState, useRef, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import { C } from '../theme'

const SUGGESTIONS = [
  '哪些商品庫存不足？',
  '目前有哪些未完成的任務？',
  '最近的採購單狀態如何？',
  '有幾張標籤還沒入庫？',
]

export default function AIAssistant({ userProfile, isMobile = false }) {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState([
    { role: 'assistant', text: '你好！我是 ERP AI 助手 🤖\n可以問我庫存、任務、客戶、採購、生產、出貨等問題。\n（回答範圍依貴公司已開通的模組而定）' }
  ])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const bottomRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const ask = async (question) => {
    if (!question.trim() || loading) return
    const q = question.trim()
    setInput('')
    setMessages(prev => [...prev, { role: 'user', text: q }])
    setLoading(true)

    try {
      const { data, error } = await supabase.functions.invoke('bright-task', {
        body: { question: q }
      })
      if (error) throw error
      if (data?.error) throw new Error(data.error)
      setMessages(prev => [...prev, { role: 'assistant', text: data.answer }])
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', text: `⚠️ 發生錯誤：${err.message}`, error: true }])
    }
    setLoading(false)
  }

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(input) }
  }

  return (
    <>
      {/* 浮動按鈕 — 膠囊形，標示「AI 助手」避免誤認為人工客服 */}
      <button
        onClick={() => setOpen(o => !o)}
        title="AI 助手（自動化，非人工客服）"
        style={{
          position: 'fixed',
          bottom: isMobile ? 96 : 24,   /* 手機版底部導航 80px + 16px 間距 */
          right: 16,
          zIndex: 1000,
          height: 44, borderRadius: 22,
          padding: open ? '0 18px' : '0 16px 0 12px',
          background: open ? '#374151' : C.accent,
          border: 'none', cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          display: 'flex', alignItems: 'center', gap: 7,
          transition: 'background 0.2s, padding 0.2s',
          whiteSpace: 'nowrap',
        }}>
        <span style={{ fontSize: 20, lineHeight: 1 }}>{open ? '✕' : '🤖'}</span>
        {!open && (
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: 0.3 }}>
            AI 助手
          </span>
        )}
      </button>

      {/* 聊天視窗 */}
      {open && (
        <div style={{
          position: 'fixed',
          bottom: isMobile ? 150 : 86,  /* 手機版：按鈕上方；桌機版：同原本 */
          right: 16,
          zIndex: 999,
          width: isMobile ? 'calc(100vw - 32px)' : 340,
          maxWidth: isMobile ? 'calc(100vw - 32px)' : 340,
          height: isMobile ? 'calc(100vh - 220px)' : 480,
          maxHeight: isMobile ? 'calc(100vh - 220px)' : 'calc(100vh - 120px)',
          background: C.panel, border: `1.5px solid ${C.border}`,
          borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          {/* 標題列 */}
          <div style={{
            padding: '12px 16px', borderBottom: `1px solid ${C.border}`,
            background: C.accent, borderRadius: '14px 14px 0 0',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{ fontSize: 18 }}>🤖</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>ERP AI 助手</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.75)' }}>庫存・任務・客戶・採購・生產・出貨</div>
            </div>
          </div>

          {/* 訊息區 */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 12px 4px' }}>
            {messages.map((m, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                marginBottom: 10,
              }}>
                <div style={{
                  maxWidth: '82%',
                  background: m.role === 'user' ? C.accent : (m.error ? '#fef2f2' : C.bg),
                  color: m.role === 'user' ? '#fff' : (m.error ? '#dc2626' : C.text),
                  border: m.role === 'user' ? 'none' : `1px solid ${m.error ? '#fca5a5' : C.border}`,
                  borderRadius: m.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                  padding: '8px 12px', fontSize: 13, lineHeight: 1.6,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {m.text}
                </div>
              </div>
            ))}

            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 10 }}>
                <div style={{
                  background: C.bg, border: `1px solid ${C.border}`,
                  borderRadius: '14px 14px 14px 4px', padding: '8px 14px',
                  fontSize: 18, letterSpacing: 2,
                }}>
                  <span style={{ animation: 'blink 1s infinite' }}>●</span>
                  <span style={{ animation: 'blink 1s 0.3s infinite', marginLeft: 3 }}>●</span>
                  <span style={{ animation: 'blink 1s 0.6s infinite', marginLeft: 3 }}>●</span>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* 快速建議（只在第一次顯示）*/}
          {messages.length === 1 && (
            <div style={{ padding: '0 12px 8px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {SUGGESTIONS.map((s, i) => (
                <button key={i} onClick={() => ask(s)}
                  style={{
                    background: 'none', border: `1px solid ${C.accent}`, borderRadius: 20,
                    padding: '4px 10px', fontSize: 11, color: C.accent, cursor: 'pointer',
                  }}>
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* 輸入區 */}
          <div style={{
            padding: '8px 10px', borderTop: `1px solid ${C.border}`,
            display: 'flex', gap: 8, alignItems: 'flex-end',
          }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="問我任何問題..."
              rows={1}
              disabled={loading}
              style={{
                flex: 1, resize: 'none', border: `1.5px solid ${C.border}`,
                borderRadius: 10, padding: '8px 10px', fontSize: 13,
                background: C.bg, color: C.text, outline: 'none',
                fontFamily: 'inherit', lineHeight: 1.5,
                maxHeight: 80, overflowY: 'auto',
              }}
            />
            <button
              onClick={() => ask(input)}
              disabled={loading || !input.trim()}
              style={{
                width: 36, height: 36, borderRadius: '50%', border: 'none',
                background: (!loading && input.trim()) ? C.accent : C.border,
                color: '#fff', fontSize: 16, cursor: (!loading && input.trim()) ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
              ↑
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes blink {
          0%, 100% { opacity: 0.2; }
          50% { opacity: 1; }
        }
      `}</style>
    </>
  )
}
